import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  CreditCardIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  LockClosedIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon
} from '../utils/icons';

// Mock data for the dashboard
const mockAccounts = [
  { id: 1, name: 'Checking', balance: 4250.75, type: 'checking' },
  { id: 2, name: 'Savings', balance: 10250.25, type: 'savings' },
  { id: 3, name: 'Credit Card', balance: -1250.50, type: 'credit' },
  { id: 4, name: 'Investment', balance: 18750.00, type: 'investment' },
];

const mockBudgets = [
  { category: 'Housing', spent: 1200, budget: 1500 },
  { category: 'Food', spent: 450, budget: 600 },
  { category: 'Transportation', spent: 180, budget: 250 },
  { category: 'Entertainment', spent: 120, budget: 200 },
];

// Calculate net worth from all accounts
const calculateNetWorth = (accounts) => {
  return accounts.reduce((sum, account) => sum + account.balance, 0);
};

const Dashboard = () => {
  const { user } = useUser();
  const navigate = useNavigate();

  const totalBalance = mockAccounts.reduce((sum, account) => sum + account.balance, 0);
  const totalSpent = mockBudgets.reduce((sum, budget) => sum + budget.spent, 0);
  const totalBudget = mockBudgets.reduce((sum, budget) => sum + budget.budget, 0);
  const remainingBudget = totalBudget - totalSpent;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Welcome back, {user?.firstName || 'User'}!</h1>
        <p className="mt-1 text-sm text-gray-500">Here's an overview of your finances</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-white p-5 rounded-xl shadow-sm lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Credit Card Rewards</h2>
            <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
              <CreditCardIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-amber-600">$0.00</p>
          <p className="text-sm text-gray-500 mt-1">Across all accounts</p>
        </div>
        
        <div className="bg-white p-5 rounded-xl shadow-sm lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Income</h2>
            <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
              <ArrowUpIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-green-600">$0.00</p>
          <p className="text-sm text-gray-500 mt-1">This month</p>
        </div>
        
        <div className="bg-white p-5 rounded-xl shadow-sm lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Expenses</h2>
            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center text-red-600">
              <ArrowDownIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-red-600">$0.00</p>
          <p className="text-sm text-gray-500 mt-1">This month</p>
        </div>
        
        <div className="bg-white p-5 rounded-xl shadow-sm lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Savings</h2>
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
              <LockClosedIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-blue-600">$0.00</p>
          <p className="text-sm text-gray-500 mt-1">This month</p>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Investments</h2>
            <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
              <ArrowTrendingUpIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-purple-600">{formatCurrency(18750.00)}</p>
          <p className="text-sm text-gray-500 mt-1">Total portfolio value</p>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm lg:col-span-1">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Net Worth</h2>
            <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
              <CurrencyDollarIcon className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-bold text-indigo-600">{formatCurrency(calculateNetWorth(mockAccounts))}</p>
          <p className="text-sm text-gray-500 mt-1">Current total</p>
        </div>
      </div>

      {/* Accounts Summary */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Accounts</h3>
        </div>
        <div className="bg-white overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {mockAccounts.map((account) => (
              <li key={account.id} className="px-6 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/accounts')}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      account.type === 'checking' ? 'bg-blue-100 text-blue-600' :
                      account.type === 'savings' ? 'bg-green-100 text-green-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {account.type === 'checking' ? 'C' : account.type === 'savings' ? 'S' : 'CC'}
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">{account.name}</p>
                      <p className="text-sm text-gray-500">{account.type}</p>
                    </div>
                  </div>
                  <p className={`text-sm font-medium ${
                    account.balance < 0 ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {formatCurrency(account.balance)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <div className="px-6 py-4 bg-gray-50 text-right">
            <button
              type="button"
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
              onClick={() => navigate('/accounts')}
            >
              View all accounts →
            </button>
          </div>
        </div>
      </div>

      {/* Budget Overview */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Budget Overview</h3>
        </div>
        <div className="px-6 py-4">
          <div className="space-y-4">
            {mockBudgets.map((budget) => {
              const percentage = (budget.spent / budget.budget) * 100;
              return (
                <div key={budget.category} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-900">{budget.category}</span>
                    <span className="text-gray-500">
                      {formatCurrency(budget.spent)} of {formatCurrency(budget.budget)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className={`h-2.5 rounded-full ${
                        percentage > 90 ? 'bg-red-500' : 
                        percentage > 75 ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 text-right">
            <button
              type="button"
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
              onClick={() => navigate('/budgets')}
            >
              View full budget →
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/accounts')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 flex flex-col items-center text-center"
            >
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mb-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-900">Add Account</span>
            </button>

            <button
              onClick={() => navigate('/budgets')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 flex flex-col items-center text-center"
            >
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 mb-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-900">Create Budget</span>
            </button>

            <button
              onClick={() => navigate('/investments')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 flex flex-col items-center text-center"
            >
              <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mb-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-900">Start Investing</span>
            </button>

            <button
              onClick={() => navigate('/debt-management')}
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 flex flex-col items-center text-center"
            >
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 mb-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-900">Manage Debt</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
